import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { verifyCenterOAuthToken } from "@/features/auth/services/centerOAuthApi";
import { getPendingProductContractApi } from "@/api/services/admin/product-contract";
import { AppRoutes } from "@/routes/config";
import { EOrganizationType } from "@/types/enum";
import { logout } from "@/utils/api";
import { redirectToLogin } from "@/utils/center";
import { showError } from "@/utils/error";
import { setToken } from "@/utils/token";
import Cookies from "js-cookie";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Only allow app-internal absolute paths so a crafted `redirectUrl` cannot bounce
// the freshly authenticated user to an external origin (open-redirect).
const getSafeRedirectPath = (raw: string | null, fallback: string): string => {
  if (raw && raw.startsWith("/") && !raw.startsWith("//")) {
    return raw;
  }
  return fallback;
};

const DEFAULT_REDIRECT_URL = AppRoutes.organizationList;

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const contractToken = searchParams.get("contractToken");
  // user was heading to before being bounced to login (stashed by /login).
  const redirectUrl = getSafeRedirectPath(searchParams.get("redirectUrl") || sessionStorage.getItem("post-login-redirect"), DEFAULT_REDIRECT_URL);

  useEffect(() => {
    const handleCallback = async () => {
      if (!token) {
        redirectToLogin();
        return;
      }

      try {
        const data = await verifyCenterOAuthToken(token);
        logout();
        if (data.accessToken && data.refreshToken) {
          setToken(data.accessToken, data.refreshToken);
          if (data.email) {
            Cookies.set("admin-email", data.email);
            Cookies.set("admin-user-id", data.email);
          }
        }

        // No staged contract on this handoff → plain login.
        if (!contractToken) {
          navigate(DEFAULT_REDIRECT_URL);
          return;
        }

        // Persist the contract token so the create/list pages can resolve the pending contract.
        Cookies.set("contract-token", contractToken);

        // Route to the matching create page based on the staged contract's organization type.
        const res = await getPendingProductContractApi({ token: contractToken });
        const organizationType = res.data.data?.organizationType;
        const isValidOrganizationType = Object.values(EOrganizationType).includes(organizationType as EOrganizationType);
        if (isValidOrganizationType) {
          navigate(AppRoutes.organizationCreate);
          return;
        }

        sessionStorage.removeItem("post-login-redirect");
        window.location.replace(redirectUrl);
      } catch (err) {
        showError(err);
        redirectToLogin();
      }
    };

    handleCallback();
  }, [token, navigate, redirectUrl, contractToken]);

  return <LoadingScreen />;
};

export default AuthCallback;
